import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import axios from 'axios';
import { sanitizeHtml } from '@/utils/sanitize';
import {
  Play, FileQuestion, BookOpen, Layers, AlignLeft,
  Move, GitMerge, Image, Headphones, Download, PenLine,
  Check, Loader2, ArrowRight, Lock, Award, ChevronLeft,
  ChevronRight as ChevronRightIcon, Clock, MessageCircle,
  ArrowUpDown, GitBranch, ShieldCheck, MapPin, RotateCcw,
} from 'lucide-react';

import { API } from '@/config';
import PlayerMatchingElement from './elements/MatchingElement';
import ProgressCheckpointElement from './elements/ProgressCheckpointElement';
import PlayerDragDropElement from './elements/DragDropElement';
import { ElementErrorBoundary } from './elements/ElementErrorBoundary';

const ELEMENT_ICONS = {
  video: Play, quiz: FileQuestion, case_study: BookOpen, flashcard: Layers,
  fill_gaps: AlignLeft, drag_drop_task: Move, matching: GitMerge,
  image_hotspot: Image, audio: Headphones, downloadable: Download,
  reflection_journal: PenLine, text: BookOpen, trainer_tip: MessageCircle,
  timeline: Clock, sort_order: ArrowUpDown, decision_tree: GitBranch,
  progress_checkpoint: ShieldCheck, label_diagram: MapPin,
};

const CoursePreviewPage = () => {
  const { courseId } = useParams();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [completed, setCompleted] = useState(new Set());

  useEffect(() => {
    axios.get(`${API}/courses/${courseId}/preview`)
      .then(res => {
        setPreview(res.data);
        // Track preview view
        const sid = sessionStorage.getItem('preview_session') || Math.random().toString(36).slice(2);
        sessionStorage.setItem('preview_session', sid);
        axios.post(`${API}/courses/${courseId}/preview/track`, { event: 'view', session_id: sid }).catch(() => {});
      })
      .catch(err => setError(err.response?.data?.detail || 'No free preview available'))
      .finally(() => setLoading(false));
  }, [courseId]);

  const elements = preview?.module?.elements || [];
  const current = elements[currentIdx];
  const allDone = elements.length > 0 && completed.size >= elements.length;
  const progressPct = elements.length ? Math.round((completed.size / elements.length) * 100) : 0;

  const markComplete = () => {
    setCompleted(prev => {
      const n = new Set([...prev, current?.id]);
      // Track element completion
      const sid = sessionStorage.getItem('preview_session') || '';
      axios.post(`${API}/courses/${courseId}/preview/track`, { event: 'element_complete', element_id: current?.id, session_id: sid }).catch(() => {});
      // Track preview completion when all done
      if (n.size >= elements.length) {
        axios.post(`${API}/courses/${courseId}/preview/track`, { event: 'preview_complete', session_id: sid }).catch(() => {});
      }
      return n;
    });
  };

  const handleEnrol = () => {
    // Track enrol click
    const sid = sessionStorage.getItem('preview_session') || '';
    axios.post(`${API}/courses/${courseId}/preview/track`, { event: 'enrol_click', session_id: sid }).catch(() => {});
    if (isAuthenticated) {
      navigate(`/course/${courseId}?from=preview`);
    } else {
      navigate(`/login?redirect=/course/${courseId}?from=preview`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <Card className="max-w-md border-slate-200">
            <CardContent className="p-8 text-center">
              <Lock className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-[#2a3e5d] mb-2">No Free Preview Available</h2>
              <p className="text-sm text-slate-500 mb-6">{error}</p>
              <Button asChild className="bg-[#20acf6] hover:bg-[#3bb8f7]">
                <Link to={`/course/${courseId}`}>View Course Details</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]" data-testid="course-preview-page">
      <Header />

      {/* Top Bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" asChild className="shrink-0">
              <Link to={`/course/${courseId}`}><ChevronLeft className="h-4 w-4 mr-1" /> Back</Link>
            </Button>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">{preview.course_title}</p>
              <p className="text-xs text-slate-500">Free Preview — {preview.module.title}</p>
            </div>
          </div>
          <Badge className="bg-[#10B981]/15 text-[#10B981] shrink-0">Free Preview</Badge>
        </div>
        <div className="max-w-5xl mx-auto px-4 pb-2">
          <Progress value={progressPct} className="h-1.5" />
        </div>
      </div>

      <main className="flex-1 max-w-4xl mx-auto px-4 py-8 w-full">
        {/* Completion CTA */}
        {allDone ? (
          <Card className="border-[#20acf6] bg-gradient-to-r from-[#20acf6]/5 to-[#02d1d4]/5 mb-8" data-testid="preview-complete-cta">
            <CardContent className="p-8 text-center">
              <Award className="h-14 w-14 text-[#20acf6] mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-[#2a3e5d] mb-2">You completed the free preview!</h2>
              <p className="text-slate-600 mb-6">
                Continue your learning — enrol now to access the remaining{' '}
                <strong>{preview.remaining_modules} module{preview.remaining_modules !== 1 ? 's' : ''}</strong> and earn your certificate.
              </p>
              <Button size="lg" className="bg-[#20acf6] hover:bg-[#3bb8f7] shadow-md shadow-[#20acf6]/20" onClick={handleEnrol} data-testid="enrol-after-preview-btn">
                <ArrowRight className="h-5 w-5 mr-2" /> Enrol Now
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Element nav */}
            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
              {elements.map((el, i) => {
                const Icon = ELEMENT_ICONS[el.type] || BookOpen;
                const done = completed.has(el.id);
                const active = i === currentIdx;
                return (
                  <button
                    key={el.id}
                    onClick={() => setCurrentIdx(i)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap shrink-0 transition-colors ${
                      active ? 'bg-[#20acf6] text-white' : done ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid={`preview-el-${i}`}
                  >
                    {done ? <Check className="h-3 w-3" /> : <Icon className="h-3 w-3" />}
                    {el.title}
                  </button>
                );
              })}
            </div>

            {/* Current element */}
            {current && (
              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">{current.title}</h3>
                  <ElementErrorBoundary key={current.id}>
                    <PreviewElement config={current.config} type={current.type} onComplete={markComplete} />
                  </ElementErrorBoundary>
                  {!completed.has(current.id) && (
                    <Button onClick={markComplete} className="mt-4 bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="preview-mark-done">
                      <Check className="h-4 w-4 mr-2" /> Mark as Complete
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Nav buttons */}
            <div className="flex justify-between mt-6">
              <Button
                variant="outline"
                disabled={currentIdx === 0}
                onClick={() => setCurrentIdx(currentIdx - 1)}
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Previous
              </Button>
              {currentIdx < elements.length - 1 ? (
                <Button
                  className="bg-[#20acf6] hover:bg-[#3bb8f7]"
                  onClick={() => setCurrentIdx(currentIdx + 1)}
                >
                  Next <ChevronRightIcon className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button className="bg-[#02d1d4] hover:bg-[#00acd0]" onClick={handleEnrol} data-testid="enrol-btn-bottom">
                  Enrol Now <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              )}
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

// ==============================================
// INTERACTIVE ELEMENT RENDERERS FOR PREVIEW
// ==============================================

const PreviewElement = ({ config, type, onComplete }) => {
  switch (type) {
    case 'text': return <TextElement config={config} />;
    case 'video': return <VideoElement config={config} />;
    case 'audio': return <AudioElement config={config} />;
    case 'downloadable': return <DownloadElement config={config} />;
    case 'flashcard': return <FlashcardElement config={config} />;
    case 'matching': return <PlayerMatchingElement config={config?.matching || config || {}} onComplete={onComplete || (() => {})} onSaveData={() => {}} isCompleted={false} />;
    case 'sort_order': return <SortElement config={config} />;
    case 'trainer_tip': return <TrainerTipElement config={config} />;
    case 'decision_tree': return <DecisionTreeElement config={config} />;
    case 'quiz': return <QuizPreviewElement config={config} />;
    case 'drag_drop_task': return <PlayerDragDropElement config={config?.drag_drop_task || config || {}} onComplete={onComplete || (() => {})} onSaveData={() => {}} isCompleted={false} />;
    case 'progress_checkpoint': return <ProgressCheckpointElement config={config?.progress_checkpoint || config || {}} onComplete={onComplete || (() => {})} onSaveData={() => {}} isCompleted={false} />;
    case 'reflection_journal': return <ReflectionElement config={config} />;
    case 'fill_gaps': return <FillGapsElement config={config} />;
    default:
      return (
        <div className="p-6 bg-slate-50 rounded-xl text-center">
          <p className="text-sm text-slate-500">Interactive element — explore to continue.</p>
        </div>
      );
  }
};

const TextElement = ({ config }) => {
  const content = config?.text?.content || config?.content || '<p>Content</p>';
  return <div className="prose prose-sm max-w-none text-slate-700" dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }} />;
};

const VideoElement = ({ config }) => {
  const url = config?.video?.url || config?.url || config?.file_url || '';
  if (url.includes('youtube') || url.includes('youtu.be')) {
    let embed = url;
    try {
      if (url.includes('youtube.com/watch')) embed = `https://www.youtube.com/embed/${new URL(url).searchParams.get('v')}`;
      else if (url.includes('youtu.be/')) embed = `https://www.youtube.com/embed/${url.split('youtu.be/')[1]}`;
    } catch {}
    return <div className="aspect-video rounded-xl overflow-hidden bg-slate-900"><iframe src={embed} className="w-full h-full" allowFullScreen title="Video" /></div>;
  }
  if (url.includes('PLACEHOLDER')) {
    return (
      <div className="aspect-video rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Play className="h-12 w-12 text-white/40 mx-auto mb-2" />
          <p className="text-sm text-white/60">{config?.video?.title || 'Video content'}</p>
        </div>
      </div>
    );
  }
  return <video controls className="w-full rounded-xl"><source src={url} /></video>;
};

const AudioElement = ({ config }) => (
  <div className="p-6 bg-slate-50 rounded-xl">
    <audio controls className="w-full"><source src={config?.audio?.file_url || config?.file_url || ''} /></audio>
    {config?.audio?.transcript && <p className="mt-3 text-sm text-slate-600">{config.audio.transcript}</p>}
  </div>
);

const DownloadElement = ({ config }) => {
  const dl = config?.downloadable || config || {};
  return (
    <div className="p-4 bg-slate-50 rounded-xl flex items-center gap-3">
      <Download className="h-6 w-6 text-[#20acf6]" />
      <div>
        <p className="font-medium text-sm text-slate-800">{dl.title || 'Resource'}</p>
        <p className="text-xs text-slate-500">{dl.description || 'Downloadable resource'}</p>
      </div>
    </div>
  );
};

const FlashcardElement = ({ config }) => {
  const cards = config?.flashcard?.cards || [];
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);

  if (cards.length === 0) return <p className="text-slate-500 text-sm">No flashcards available.</p>;
  const card = cards[idx];

  return (
    <div className="space-y-4">
      <div
        onClick={() => setFlipped(!flipped)}
        className="min-h-[180px] p-6 rounded-xl border-2 border-slate-200 cursor-pointer hover:border-[#20acf6] transition-all flex items-center justify-center text-center"
        data-testid="flashcard-preview"
      >
        <div>
          <p className="text-xs text-slate-400 mb-2">{flipped ? 'ANSWER' : 'TERM'} — tap to flip</p>
          <p className={`text-lg font-medium ${flipped ? 'text-[#20acf6]' : 'text-[#2a3e5d]'}`}>
            {flipped ? (card.back || card.back_text || '') : (card.front || card.front_text || '')}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500">{idx + 1} / {cards.length}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={idx === 0} onClick={() => { setIdx(idx - 1); setFlipped(false); }}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" disabled={idx === cards.length - 1} onClick={() => { setIdx(idx + 1); setFlipped(false); }}>
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const SortElement = ({ config }) => {
  const sortConfig = config?.sort_order || {};
  const items = sortConfig.items || [];
  const [order, setOrder] = useState(() => [...items].sort(() => Math.random() - 0.5));
  const [checked, setChecked] = useState(false);

  const moveUp = (i) => {
    if (i === 0 || checked) return;
    const n = [...order];
    [n[i - 1], n[i]] = [n[i], n[i - 1]];
    setOrder(n);
  };
  const moveDown = (i) => {
    if (i === order.length - 1 || checked) return;
    const n = [...order];
    [n[i], n[i + 1]] = [n[i + 1], n[i]];
    setOrder(n);
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">{sortConfig.instructions || 'Drag items into the correct order:'}</p>
      <div className="space-y-2">
        {order.map((item, i) => {
          const isCorrect = checked && item === items[i];
          return (
            <div key={i} className={`flex items-center gap-2 p-3 rounded-lg border text-sm ${
              checked ? (isCorrect ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200') : 'bg-white border-slate-200'
            }`}>
              <span className="w-6 h-6 flex items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-500">{i + 1}</span>
              <span className="flex-1 text-slate-700">{item}</span>
              {!checked && (
                <div className="flex gap-1">
                  <button onClick={() => moveUp(i)} disabled={i === 0} className="p-1 text-slate-400 hover:text-[#20acf6] disabled:opacity-30">▲</button>
                  <button onClick={() => moveDown(i)} disabled={i === order.length - 1} className="p-1 text-slate-400 hover:text-[#20acf6] disabled:opacity-30">▼</button>
                </div>
              )}
              {checked && (isCorrect ? <Check className="h-4 w-4 text-green-600" /> : <span className="text-red-500 text-xs">✗</span>)}
            </div>
          );
        })}
      </div>
      <div className="flex gap-2">
        {!checked && <Button size="sm" className="bg-[#20acf6] hover:bg-[#3bb8f7]" onClick={() => setChecked(true)}>Check Order</Button>}
        {checked && <Button size="sm" variant="outline" onClick={() => { setOrder([...items].sort(() => Math.random() - 0.5)); setChecked(false); }}><RotateCcw className="h-3.5 w-3.5 mr-1" /> Try Again</Button>}
      </div>
    </div>
  );
};

const TrainerTipElement = ({ config }) => {
  const tip = { ...(config || {}), ...(config?.trainer_tip || {}) };
  return (
    <div className="flex gap-4 p-5 bg-gradient-to-r from-[#20acf6]/5 to-[#02d1d4]/5 rounded-xl border border-[#20acf6]/20">
      <div className="w-12 h-12 rounded-full bg-[#20acf6]/20 flex items-center justify-center shrink-0">
        <MessageCircle className="h-6 w-6 text-[#20acf6]" />
      </div>
      <div>
        <p className="text-xs font-semibold text-[#20acf6] mb-1 uppercase tracking-wide">Ace says</p>
        <div className="text-sm text-slate-700 leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(tip.text || "Keep going! You're doing great.") }} />
      </div>
    </div>
  );
};

const DecisionTreeElement = ({ config }) => {
  const tree = config?.decision_tree || {};
  const nodes = tree.nodes || [];
  const [currentNode, setCurrentNode] = useState('start');
  const [history, setHistory] = useState([]);

  const node = nodes.find(n => n.id === currentNode) || nodes[0];
  if (!node) return <p className="text-slate-500 text-sm">No scenario available.</p>;

  const handleChoice = (choice) => {
    setHistory(h => [...h, currentNode]);
    setCurrentNode(choice.next_node);
  };

  const restart = () => {
    setCurrentNode('start');
    setHistory([]);
  };

  return (
    <div className="space-y-4">
      {tree.title && <p className="text-xs font-semibold text-[#2a3e5d] uppercase tracking-wide">{tree.title}</p>}
      <div className={`p-5 rounded-xl border-2 ${node.is_end ? 'border-[#20acf6] bg-[#20acf6]/5' : 'border-slate-200 bg-white'}`}>
        <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-line">{node.text}</p>
      </div>
      {node.choices && !node.is_end && (
        <div className="space-y-2">
          {node.choices.map((c, i) => (
            <button
              key={i}
              onClick={() => handleChoice(c)}
              className="w-full text-left p-3 rounded-lg border border-slate-200 bg-white hover:border-[#20acf6] hover:bg-[#20acf6]/5 transition-colors text-sm text-slate-700"
              data-testid={`dt-choice-${i}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}
      {(node.is_end || history.length > 0) && (
        <Button variant="outline" size="sm" onClick={restart}>
          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Start Over
        </Button>
      )}
    </div>
  );
};

const QuizPreviewElement = ({ config }) => {
  const questions = config?.quiz?.questions || [];
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);

  if (questions.length === 0) return <p className="text-slate-500 text-sm">No quiz questions.</p>;
  const optionsOf = (q) => (q.type === 'true_false' && !(q.options || []).length)
    ? [{ text: 'True', is_correct: String(q.correct_answer).toLowerCase() === 'true' }, { text: 'False', is_correct: String(q.correct_answer).toLowerCase() === 'false' }]
    : (q.options || []);
  const correctIdx = (q) => {
    const opts = optionsOf(q);
    const i = opts.findIndex(o => o && (o.is_correct || o.isCorrect));
    if (i >= 0) return i;
    if (typeof q.correct === 'number') return q.correct;
    return opts.findIndex(o => o && String(o.id ?? o.value) === String(q.correct_answer));
  };
  const q = questions[idx];

  const score = questions.reduce((s, q, i) => s + (answers[i] === correctIdx(q) ? 1 : 0), 0);

  if (showResult) {
    const pct = Math.round((score / questions.length) * 100);
    return (
      <div className="text-center p-6">
        <p className="text-3xl font-bold text-[#2a3e5d] mb-2">{pct}%</p>
        <p className="text-sm text-slate-600 mb-4">{score} / {questions.length} correct</p>
        <Button variant="outline" size="sm" onClick={() => { setIdx(0); setAnswers({}); setShowResult(false); }}>
          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">Question {idx + 1} of {questions.length}</p>
      <p className="text-sm font-medium text-slate-800">{q.question || q.question_text}</p>
      <div className="space-y-2">
        {optionsOf(q).map((opt, oi) => {
          const label = typeof opt === 'string' ? opt : opt.text || opt.label || '';
          const selected = answers[idx] === oi;
          return (
            <button
              key={oi}
              data-testid={`preview-quiz-option-${idx}-${oi}`}
              onClick={() => setAnswers(a => ({ ...a, [idx]: oi }))}
              className={`w-full text-left p-3 rounded-lg border text-sm transition-colors ${
                selected ? 'border-[#20acf6] bg-[#20acf6]/5 text-[#2a3e5d]' : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
      <div className="flex justify-between">
        <Button variant="outline" size="sm" data-testid="preview-quiz-prev-btn" disabled={idx === 0} onClick={() => setIdx(idx - 1)}>Previous</Button>
        {idx < questions.length - 1 ? (
          <Button size="sm" className="bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="preview-quiz-next-btn" disabled={answers[idx] === undefined} onClick={() => setIdx(idx + 1)}>Next</Button>
        ) : (
          <Button size="sm" className="bg-[#02d1d4] hover:bg-[#02bfc2]" disabled={Object.keys(answers).length < questions.length} data-testid="preview-quiz-submit-btn" onClick={() => setShowResult(true)}>Submit</Button>
        )}
      </div>
    </div>
  );
};

const ReflectionElement = ({ config }) => {
  const rc = config?.reflection_journal || {};
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600 whitespace-pre-line">{rc.prompt || 'Write your reflection here.'}</p>
      <textarea
        className="w-full border border-slate-200 rounded-lg p-3 text-sm resize-none focus:ring-2 focus:ring-[#20acf6] focus:border-transparent"
        rows={4}
        placeholder="Your thoughts..."
        data-testid="preview-reflection"
      />
    </div>
  );
};

const FillGapsElement = ({ config }) => {
  const fg = config?.fill_gaps || {};
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">Fill in the blanks in the text below:</p>
      <div className="p-4 bg-slate-50 rounded-xl text-sm text-slate-700 leading-relaxed">
        {fg.template_text || 'Template text with {gaps}'}
      </div>
      {fg.word_options?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {fg.word_options.map((w, i) => (
            <span key={i} className="px-3 py-1 bg-[#20acf6]/10 text-[#20acf6] rounded-full text-xs font-medium">{w}</span>
          ))}
        </div>
      )}
    </div>
  );
};

export default CoursePreviewPage;
